import { useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import makeStyles from '@mui/styles/makeStyles'
import { ArchiveRestoreEvent } from 'Components/_common/ArchiveEvent/ArchiveRestoreEvent'
import CustomHelmet from 'Components/_common/CustomHelmet'
import MobileMainHeader from 'Components/_common/MobileMainHeader'
import NotFound from 'Components/_common/NotFound'
import Timer from 'Components/_common/Timer'
import { useAuth, useIsAdmin } from 'Components/Auth'
import ContestQualificationMapCheckModal from 'Components/ContestComponents/ContestQualification/ContestQualificationMapCheckModal'
import ContestSubmitMapDialog from 'Components/ContestComponents/ContestSubmitMapDialog'
import ContestActions from 'Components/ContestComponents/ContestView/ContestActions'
import ContestSceneDesktop from 'Components/ContestComponents/ContestView/ContestSceneDesktop'
import ContestSceneMobile from 'Components/ContestComponents/ContestView/ContestSceneMobile'
import {
  CONTEST_MOBILE_STATES,
  getContestMobileHeaderName,
  getContestSceneStyles,
  getContestStepperState,
  getIsEvaluationRound,
  getIsFinishedRound,
  getIsPublishedRound,
  getIsQualificationRound,
  getIsRoundValidForCommunityVote,
  getIsSubmissionRound,
  getIsVoteRound,
  getRoundMapListQueryState,
  isJudgeAllowedForTheRound,
  isUserAJudgeInTheContest,
} from 'Components/ContestComponents/utils'
import {
  getIsCommunityVoteConfirmed,
  useContestBackground,
  useContestCommunityVoteQuery,
  useContestJudges,
  useContestMapsByStateQuery,
  useContestQuery,
  useContestVoteRoundQuery,
  useContestWinnersListQuery,
  useDeleteContestMutation,
  useLikeContestMutation,
  useUnlikeContestMutation,
} from 'Queries/Contests'
import { useCategoryName, useGameName, useGameTypeName, usePlayersCountName } from 'Queries/Dictionaries'
import { useUserQuery } from 'Queries/Users'
import { useMemo, useState } from 'react'
import { Else, If, Then, When } from 'react-if'
import { HashRouter, Route, useParams } from 'react-router-dom'
import { ROUTE_QUALIFY_MAP_HASH, ROUTE_SUBMIT_MAP_HASH } from 'Scenes/constants'
import Button from 'Theme/Button'
import FAIcon from 'Theme/FAIcon'
import { FA_ICONS } from 'Theme/font-awesome-icon-constants'
import { BREAKPOINT_MIN_MD, BREAKPOINT_MIN_SM } from 'Theme/stylesUtil'

import { ArchiveRestoreContestModal } from '../../../Components/ContestComponents/ArchiveRestoreContestModal'
import { IS_DEV_ENV } from '../../../constants'
import { transformTextWithThreeDots } from '../../../utils/text-transformation-utils'
import {
  navigateToBack,
  navigateToEditContestPage,
  navigateToEventsListPage,
  navigateToLoginModal,
  navigateToSubmitMapModal,
} from '../../navigators'

const useStyles = makeStyles(getContestSceneStyles)
const { MAIN, SUBMIT_MAP, EDIT_SUBMIT, COMMUNITY_FAVORITES } = CONTEST_MOBILE_STATES

const SceneContest = () => {
  const classes = useStyles()
  const { id } = useParams()
  const { id: userId, isAuthorized } = useAuth()
  const isAdmin = useIsAdmin()
  const { data: contest = {}, isFetched: isContestFetched } = useContestQuery(id)
  const { data: owner = {} } = useUserQuery(contest.owner_id)
  const { mutate: likeContest } = useLikeContestMutation()
  const { mutate: unlikeContest } = useUnlikeContestMutation()
  const { mutateAsync: deleteContestMutation } = useDeleteContestMutation()
  const queryState = getRoundMapListQueryState(contest.state, true)
  const { data: submissionMaps = [], isFetched: isSubmissionMapsFetched } = useContestMapsByStateQuery(
    contest.id,
    queryState,
    true,
  )

  const [mobileViewState, setMobileViewState] = useState('')

  const gameTitle = useGameName(contest.game_id)
  const categoryName = useCategoryName(contest.category_id)
  const mapPlayers = usePlayersCountName(contest.players_count_id)
  const judges = useContestJudges(contest)
  const background = useContestBackground(contest)
  const gameTypeNames = useGameTypeName(contest.game_types)

  const contestState = contest.state
  const contestId = contest.id
  const isOwner = userId === owner.id
  const isInvited = contest.is_invited_judge
  const isJudge = isUserAJudgeInTheContest(userId, judges)
  const isContestant = !isOwner && !isJudge
  const isSubmissionRound = getIsSubmissionRound(contestState)
  const isQualificationRound = getIsQualificationRound(contestState)
  const isEvaluationRound = getIsEvaluationRound(contestState)
  const isVoteRound = getIsVoteRound(contestState)
  const isFinishedRound = getIsFinishedRound(contestState)
  const isPublishedRound = getIsPublishedRound(contestState)
  const isWithCommunityVote = !!contest.rewards?.find((item) => !!item.is_community)

  const { data: communityVote } = useContestCommunityVoteQuery(
    getIsRoundValidForCommunityVote(contestState) && contestId,
  )

  const { data: roundData } = useContestVoteRoundQuery(isEvaluationRound && contestId, contestState)
  const isJudgeForTheRound = isJudgeAllowedForTheRound(userId, contest, roundData?.current_round_limit)

  const communityVoteTimeLeft = communityVote?.minutes_left || 0
  const isContestantVoted = useMemo(() => getIsCommunityVoteConfirmed(communityVote, userId), [communityVote, userId])
  const isCommunityVoteRound =
    isAuthorized && isContestant && !isContestantVoted && getIsRoundValidForCommunityVote(contestState)

  const { data: winners, isFetched: isWinnersFetched } = useContestWinnersListQuery(
    id,
    (id && !isCommunityVoteRound && isFinishedRound) || isPublishedRound,
  )
  const isBiggerThanSMScreen = useMediaQuery(BREAKPOINT_MIN_SM)
  const isBiggerThanMDScreen = useMediaQuery(BREAKPOINT_MIN_MD)
  const hasSubmissionMaps = submissionMaps?.data?.length || 0
  const showSubmitMapButton = !(isOwner || isJudge) && isSubmissionRound
  const showSubmissionsRoundList = isSubmissionRound || (isContestant && !isPublishedRound && !isCommunityVoteRound)
  const showSubmissionsRoundListMobile = isSubmissionRound || (isContestant && !isPublishedRound)
  const showRoundInfo = (isAuthorized && isJudgeForTheRound) || isCommunityVoteRound
  const showQualificationList = showRoundInfo && isQualificationRound
  const showJudgesMapsVote = showRoundInfo && isJudgeForTheRound && isVoteRound
  const isShowWinnersList = (isJudge && isFinishedRound) || isPublishedRound

  const handleLikeContest = () => {
    if (!isAuthorized) {
      navigateToLoginModal()
    } else if (contest.is_like) {
      unlikeContest(id)
    } else {
      likeContest(id)
    }
  }

  const handleSubmitMapClick = () => {
    if (!isAuthorized) {
      navigateToLoginModal()
    } else {
      if (!isBiggerThanSMScreen && !hasSubmissionMaps) {
        setMobileViewState(SUBMIT_MAP)
      }
      if (!isBiggerThanSMScreen && hasSubmissionMaps) {
        setMobileViewState(EDIT_SUBMIT)
      }
      if (isBiggerThanSMScreen) {
        navigateToSubmitMapModal()
      }
    }
  }

  // NOTE this should not be used in production, only for dev purposes
  const showDeleteContestButton = isOwner && IS_DEV_ENV && false
  const handleDeleteContest = () => {
    deleteContestMutation(id).then(() => navigateToBack())
  }

  const DeleteContestButton = () =>
    showDeleteContestButton && (
      <Box sx={{ position: 'absolute', top: '5%', left: '50%', transform: 'transitionX(-50%)' }}>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteContest}
        >
          Delete Event
        </Button>
      </Box>
    )
  // TODO in future { contest.is_hidden && !isOwner } should be replaced with request sort logic
  return contest.is_hidden && !isOwner ? (
    <Container>
      <NotFound />
    </Container>
  ) : (
    <>
      <CustomHelmet
        title={contest.title}
        description={contest.description}
        image={background}
      />

      <DeleteContestButton />
      <MobileMainHeader
        children={
          <>
            <When condition={MAIN === mobileViewState || SUBMIT_MAP === mobileViewState}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box
                  sx={{ height: '56px', display: 'flex', alignItems: 'center', padding: '0 24px 0 16px', gap: '24px' }}
                >
                  <FAIcon
                    iconClassName={FA_ICONS.ARROW_LEFT}
                    color="text.tertiary"
                    onClick={() => navigateToEventsListPage()}
                  />
                  <Box>
                    <Typography variant="h5">{transformTextWithThreeDots(contest.title, 15)}</Typography>
                    <Box sx={{ display: 'flex', alignItem: 'center', gap: '5px' }}>
                      <Box
                        height="9px"
                        width="9px"
                        borderRadius="50%"
                        backgroundColor="primary.main"
                        position="relative"
                        top="3px"
                      />
                      <Typography
                        variant="subtitle2"
                        color="text.tertiary"
                        paddingRight="5px"
                      >
                        {getContestMobileHeaderName(contest)}
                      </Typography>
                      {getContestStepperState(contest.state) === 0 && contest.time_add_maps_countdown_sec && (
                        <Timer seconds={contest.time_add_maps_countdown_sec} />
                      )}
                    </Box>
                  </Box>
                </Box>
                {(isAdmin || isOwner) && (
                  <>
                    {contest.is_soft_deleted && (
                      <ArchiveRestoreEvent
                        renderRestoreModal={(props) => (
                          <ArchiveRestoreContestModal
                            eventId={contest.id}
                            eventTypeName="contest"
                            {...props}
                          />
                        )}
                      />
                    )}

                    <ContestActions
                      contentId={contestId}
                      isOwner={isOwner}
                      type="contest"
                      goToEditPage={navigateToEditContestPage}
                    />
                  </>
                )}
              </Box>
            </When>
            <When condition={EDIT_SUBMIT === mobileViewState}>
              <Box
                sx={{ height: '56px', display: 'flex', alignItems: 'center', padding: '0 24px 0 16px', gap: '24px' }}
              >
                <FAIcon
                  iconClassName={FA_ICONS.ARROW_LEFT}
                  color="text.tertiary"
                  onClick={() => setMobileViewState(MAIN)}
                />
                <Typography variant="h5">My Contest Maps</Typography>
              </Box>
            </When>
            <When condition={COMMUNITY_FAVORITES === mobileViewState}>
              <Box
                sx={{ height: '56px', display: 'flex', alignItems: 'center', padding: '0 24px 0 16px', gap: '24px' }}
              >
                <FAIcon
                  iconClassName={FA_ICONS.ARROW_LEFT}
                  color="text.tertiary"
                  onClick={() => setMobileViewState(MAIN)}
                />
                <Typography variant="h5">Community Vote!</Typography>
              </Box>
            </When>
          </>
        }
      />
      <If condition={isBiggerThanSMScreen}>
        <Then>
          <ContestSceneDesktop
            background={background}
            contest={contest}
            isContestFetched={isContestFetched}
            isWithCommunityVote={isWithCommunityVote}
            isCommunityVoteRound={isCommunityVoteRound}
            showJudgesMapsVote={showJudgesMapsVote}
            showSubmitMapButton={showSubmitMapButton}
            hasSubmissionMaps={hasSubmissionMaps}
            handleLikeContest={handleLikeContest}
            contestId={contestId}
            categoryName={categoryName}
            isContestant={isContestant}
            mapPlayers={mapPlayers}
            gameTypeNames={gameTypeNames}
            owner={owner}
            contestState={contestState}
            communityVoteTimeLeft={communityVoteTimeLeft}
            isJudge={isJudge}
            judges={judges}
            gameTitle={gameTitle}
            handleSubmitMapClick={handleSubmitMapClick}
            isFinishedRound={isFinishedRound}
            isShowWinnersList={isShowWinnersList}
            isWinnersFetched={isWinnersFetched}
            showQualificationList={showQualificationList}
            showRoundInfo={showRoundInfo}
            winners={winners}
            showSubmissionsRoundList={showSubmissionsRoundList}
            userId={userId}
            classes={classes}
            isInvited={isInvited}
            isPublishedRound={isPublishedRound}
            isBiggerThanMDScreen={isBiggerThanMDScreen}
            currentRoundLimit={roundData?.current_round_limit}
          />
        </Then>
        <Else>
          <ContestSceneMobile
            background={background}
            contest={contest}
            isContestFetched={isContestFetched}
            isWithCommunityVote={isWithCommunityVote}
            isCommunityVoteRound={isCommunityVoteRound}
            showJudgesMapsVote={showJudgesMapsVote}
            showSubmitMapButton={showSubmitMapButton}
            hasSubmissionMaps={hasSubmissionMaps}
            handleLikeContest={handleLikeContest}
            contestId={contestId}
            categoryName={categoryName}
            isContestant={isContestant}
            mapPlayers={mapPlayers}
            gameTypeNames={gameTypeNames}
            owner={owner}
            contestState={contestState}
            communityVoteTimeLeft={communityVoteTimeLeft}
            isJudge={isJudge}
            judges={judges}
            gameTitle={gameTitle}
            handleSubmitMapClick={handleSubmitMapClick}
            isFinishedRound={isFinishedRound}
            isShowWinnersList={isShowWinnersList}
            isWinnersFetched={isWinnersFetched}
            showQualificationList={showQualificationList}
            showRoundInfo={showRoundInfo}
            winners={winners}
            showSubmissionsRoundListMobile={showSubmissionsRoundListMobile}
            userId={userId}
            classes={classes}
            mobileViewState={mobileViewState}
            setMobileViewState={setMobileViewState}
            submissionMaps={submissionMaps}
            isSubmissionMapsFetched={isSubmissionMapsFetched}
            isInvited={isInvited}
            isPublishedRound={isPublishedRound}
            currentRoundLimit={roundData?.current_round_limit}
          />
        </Else>
      </If>

      <HashRouter hashType="noslash">
        <Route path={`/${ROUTE_SUBMIT_MAP_HASH}`}>
          <ContestSubmitMapDialog
            contest={contest}
            submissionMaps={submissionMaps}
            isSubmissionMapsFetched={isSubmissionMapsFetched}
          />
        </Route>
        <Route path={`/${ROUTE_QUALIFY_MAP_HASH}`}>
          <ContestQualificationMapCheckModal contestId={contestId} />
        </Route>
      </HashRouter>
    </>
  )
}
export default SceneContest
